import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { restartCssAnimations } from "../utils/restart_css_animations"

export default class extends Controller {
  static targets = ["modal"]

  connect() {
    // When opened from inside #taskModal (task index → task modal → attach/associate),
    // stack above it without a second backdrop — same pattern as quote_request_controller.
    const taskModalEl = document.getElementById("taskModal")
    this._nestedInTaskModal = Boolean(taskModalEl?.classList.contains("show"))
    const opts = this._nestedInTaskModal ? { backdrop: false, focus: true } : {}

    this.modal = Modal.getOrCreateInstance(this.element, opts)

    if (this._nestedInTaskModal) {
      this._taskModalEl = taskModalEl
      this.element.classList.add("modal--above-task")
      this._taskModalEl.classList.add("has-nested-modal")
      // Dismiss (X / Escape / submit) hides without removing the element from the DOM,
      // so disconnect() never runs — clear dimming here like quote_request_controller.
      this._onHidden = () => this._clearNestedTaskModal()
      this.element.addEventListener("hidden.bs.modal", this._onHidden)
    }

    // Safari: spinner inside this modal may have been injected while hidden;
    // restart CSS animations once the dialog is actually shown.
    this._onShown = () => restartCssAnimations(this.element)
    this.element.addEventListener("shown.bs.modal", this._onShown)
    this.modal.show()
    this._watchFormSubmit()
  }

  disconnect() {
    this.element.removeEventListener("shown.bs.modal", this._onShown)
    if (this._onHidden) {
      this.element.removeEventListener("hidden.bs.modal", this._onHidden)
      this._onHidden = null
    }
    this._clearNestedTaskModal()

    const instance = Modal.getInstance(this.element)
    if (instance) {
      instance.hide()
      instance.dispose()
    }
    this._cleanupModalBodyState()
  }

  _clearNestedTaskModal() {
    if (!this._nestedInTaskModal) return
    this._taskModalEl?.classList.remove("has-nested-modal")
    this.element.classList.remove("modal--above-task")
    this._nestedInTaskModal = false
    this._taskModalEl = null
  }

  _cleanupModalBodyState() {
    // Nested case: #taskModal (or another dialog) is still open — keep body lock
    // and its backdrop. Removing them would leave the outer modal unusable.
    if (document.querySelector(".modal.show")) return

    if (!document.body.classList.contains("modal-open")) return

    document.querySelectorAll(".modal-backdrop").forEach(el => el.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
  }

  hideBeforeRender(event) {
    if (this.isOpen()) {
      event.preventDefault()
      this.element.addEventListener('hidden.bs.modal', event.detail.resume)
      this.modal.hide()
    }
  }

  hideModal() {
    this.modal.hide()
  }

  isOpen() {
    return this.element.classList.contains("show")
  }

  closeWithKeyboard(e) {
    if (e.code == "Escape") {
      this.hideModal()
    }
  }

  closeBackground(e) {
    if (e && this.modalTarget.contains(e.target)) {
      return;
    }
    this.hideModal()
  }

  submit(e) {
    this.modalTarget.querySelector('form').requestSubmit()
  }

  submitEnd(e) {
    if (e.detail.success) {
      if (e.params.returnUrl) {
        Turbo.visit(e.params.returnUrl, { frame: "modal"})
      } else {
        this.hideModal()
      }
    }
  }

  back(event) {
    Turbo.visit(event.params.returnUrl, { frame: "modal"})
  }

  // Show spinner on submit buttons inside the modal
  _watchFormSubmit() {
    this.element.querySelectorAll('form').forEach(form => {
      form.addEventListener('submit', () => {
        form.querySelectorAll('[type=submit]').forEach(btn => {
          btn.disabled = true
          btn.dataset.originalText = btn.innerHTML
          btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1" role="status"></span>${btn.dataset.originalText || ''}`
        })
      })

      form.addEventListener('turbo:submit-end', () => {
        form.querySelectorAll('[type=submit]').forEach(btn => {
          btn.disabled = false
          if (btn.dataset.originalText) {
            btn.innerHTML = btn.dataset.originalText
          }
        })
      })
    })
  }
}
